import { mine as boosts } from '~/actions/App/Http/Controllers/PodcastBoostController';
import { Link, usePage } from '@inertiajs/react';
import { type ReactNode, useEffect, useState } from 'react';
import { ChevronRight, Moon, Sun } from 'lucide-react';
import { cn } from '../lib/utils';

declare global {
    interface Window {
        __tcfapi?: (command: string, version: number, callback: () => void) => void;
    }
}

interface MenuCategory {
    name: string;
    slug: string;
}

interface DashboardPageProps {
    auth?: {
        user?: {
            id: number;
            name: string;
            email: string;
        } | null;
    };
    flash?: {
        success?: string;
        error?: string;
    };
    siteMenu?: {
        categories?: MenuCategory[];
    };
    features?: {
        podcastBoost?: boolean;
    };
    [key: string]: unknown;
}

interface FlashToastState {
    variant: 'success' | 'error';
    message: string;
    sequence: number;
}

interface SiteMenuDrawerProps {
    categories: MenuCategory[];
    dark: boolean;
    open: boolean;
    onCookieSettings: () => void;
    onToggleTheme: () => void;
    showBoosts?: boolean;
    user?: { id: number; name: string; email: string } | null;
}

export function PoddtoppenLogo({
    className,
    color = '#FFF',
    height = 38,
    width = 180,
}: {
    className?: string;
    color?: string;
    height?: number;
    width?: number;
}) {
    return (
        <svg
            width={width}
            height={height}
            viewBox="0 0 726 153"
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
            className={className}
        >
            <title>Poddtoppen.se</title>
            <use xlinkHref="#pt-path0" transform="translate(181 5)" fill={color} />
            <use xlinkHref="#pt-path1" transform="translate(417 43)" fill={color} />
            <g fill={color}>
                <use xlinkHref="#pt-path2" transform="translate(0 63)" />
                <use xlinkHref="#pt-path2" transform="translate(128 63)" />
                <use xlinkHref="#pt-path3" transform="translate(32 49)" />
                <use xlinkHref="#pt-path4" transform="translate(64)" />
                <use xlinkHref="#pt-path3" transform="translate(96 23)" />
            </g>
            <defs>
                <path
                    id="pt-path0"
                    d="M33.174 44.135c4.462 0 8.5037 1.164 12.125 3.492 3.6213 2.2633 6.4667 5.432 8.536 9.506 2.134 4.074 3.201 8.6977 3.201 13.871 0 5.1733-1.0347 9.8293-3.104 13.968-2.0693 4.074-4.9147 7.275-8.536 9.603-3.5567 2.2633-7.5013 3.395-11.834 3.395-3.104 0-6.0463-.6143-8.827-1.843-2.716-1.2933-4.9793-2.8453-6.79-4.656v19.012c0 1.681-.5497 3.072-1.649 4.171-1.0993 1.164-2.4897 1.746-4.171 1.746-1.6813 0-3.07167-.55-4.171-1.649-1.09933-1.099-1.649-2.522-1.649-4.268V51.022c0-1.6813.54967-3.0717 1.649-4.171 1.09933-1.164 2.4897-1.746 4.171-1.746 1.6813 0 3.0717.582 4.171 1.746 1.0993 1.0993 1.649 2.4897 1.649 4.171v.388c1.552-1.94 3.7183-3.6213 6.499-5.044 2.7807-1.4873 5.6907-2.231 8.73-2.231zM31.719 87.3c4.1387 0 7.5337-1.552 10.185-4.656 2.6513-3.104 3.977-6.984 3.977-11.64 0-4.656-1.3257-8.5037-3.977-11.543-2.5867-3.104-5.9817-4.656-10.185-4.656-4.2033 0-7.6307 1.552-10.282 4.656-2.6513 3.0393-3.977 6.887-3.977 11.543s1.3257 8.536 3.977 11.64c2.6513 3.104 6.0787 4.656 10.282 4.656zm83.88-16.199c0 5.1733-1.164 9.8293-3.492 13.968-2.328 4.074-5.529 7.2427-9.603 9.506-4.0098 2.2633-8.4395 3.395-13.2895 3.395-4.9146 0-9.3766-1.1317-13.386-3.395-4.0093-2.2633-7.178-5.432-9.506-9.506-2.328-4.1387-3.492-8.7947-3.492-13.968 0-5.1733 1.164-9.797 3.492-13.871 2.328-4.1387 5.4967-7.3397 9.506-9.603 4.0094-2.328 8.4714-3.492 13.386-3.492 4.85 0 9.2797 1.164 13.2895 3.492 4.074 2.2633 7.275 5.4643 9.603 9.603 2.328 4.074 3.492 8.6977 3.492 13.871zm-11.64 0c0-3.1687-.679-5.9817-2.037-8.439-1.294-2.522-3.0721-4.462-5.3355-5.82-2.1986-1.358-4.656-2.037-7.372-2.037s-5.2056.679-7.469 2.037c-2.1986 1.358-3.977 3.298-5.335 5.82-1.2933 2.4573-1.94 5.2703-1.94 8.439 0 3.1687.6467 5.9817 1.94 8.439 1.358 2.4573 3.1364 4.365 5.335 5.723 2.2634 1.358 4.753 2.037 7.469 2.037s5.1734-.679 7.372-2.037c2.2634-1.358 4.0415-3.2657 5.3355-5.723 1.358-2.4573 2.037-5.2703 2.037-8.439zm62.396-45.881c1.681 0 3.071.5497 4.171 1.649 1.099 1.0993 1.649 2.522 1.649 4.268v59.946c0 1.6813-.55 3.104-1.649 4.268-1.1 1.0993-2.49 1.649-4.171 1.649-1.682 0-3.072-.5497-4.171-1.649-1.035-1.0993-1.585-2.4897-1.649-4.171-1.617 1.8753-3.816 3.492-6.596 4.85-2.716 1.2933-5.594 1.94-8.633 1.94-4.462 0-8.504-1.1317-12.125-3.395-3.622-2.328-6.499-5.529-8.633-9.603-2.07-4.074-3.104-8.6977-3.104-13.871 0-5.1733 1.034-9.797 3.104-13.871 2.069-4.1387 4.882-7.3397 8.439-9.603 3.621-2.328 7.598-3.492 11.931-3.492 3.104 0 6.014.582 8.73 1.746 2.716 1.0993 5.011 2.522 6.887 4.268V31.137c0-1.6813.549-3.0717 1.649-4.171 1.099-1.164 2.489-1.746 4.171-1.746zM146.761 87.3c4.203 0 7.63-1.5197 10.282-4.559 2.651-3.104 3.977-6.984 3.977-11.64 0-4.656-1.326-8.536-3.977-11.64-2.652-3.104-6.079-4.656-10.282-4.656-4.139 0-7.534 1.552-10.185 4.656-2.652 3.104-3.977 6.984-3.977 11.64 0 4.656 1.293 8.536 3.88 11.64 2.651 3.0393 6.078 4.559 10.282 4.559zm76.149-62.08c1.681 0 3.072.5497 4.171 1.649 1.099 1.0993 1.649 2.522 1.649 4.268v59.946c0 1.6813-.55 3.104-1.649 4.268-1.099 1.0993-2.49 1.649-4.171 1.649s-3.072-.5497-4.171-1.649c-1.035-1.0993-1.584-2.4897-1.649-4.171-1.617 1.8753-3.815 3.492-6.596 4.85-2.716 1.2933-5.594 1.94-8.633 1.94-4.462 0-8.504-1.1317-12.125-3.395-3.621-2.328-6.499-5.529-8.633-9.603-2.069-4.074-3.104-8.6977-3.104-13.871 0-5.1733 1.035-9.797 3.104-13.871 2.069-4.1387 4.882-7.3397 8.439-9.603 3.621-2.328 7.598-3.492 11.931-3.492 3.104 0 6.014.582 8.73 1.746 2.716 1.0993 5.012 2.522 6.887 4.268V31.137c0-1.6813.55-3.0717 1.649-4.171 1.099-1.164 2.49-1.746 4.171-1.746zM203.316 87.3c4.203 0 7.631-1.5197 10.282-4.559 2.651-3.104 3.977-6.984 3.977-11.64 0-4.656-1.326-8.536-3.977-11.64-2.651-3.104-6.079-4.656-10.282-4.656-4.139 0-7.534 1.552-10.185 4.656-2.651 3.104-3.977 6.984-3.977 11.64 0 4.656 1.293 8.536 3.88 11.64 2.651 3.0393 6.079 4.559 10.282 4.559zm60.145-1.261c.905 0 1.713.4527 2.425 1.358.776.8407 1.164 1.9723 1.164 3.395 0 1.746-.97 3.2333-2.91 4.462-1.876 1.164-4.01 1.746-6.402 1.746-4.01 0-7.405-.8407-10.185-2.522-2.716-1.746-4.074-5.3997-4.074-10.961V56.745h-4.462c-1.552 0-2.846-.5173-3.88-1.552-1.035-1.0347-1.552-2.328-1.552-3.88 0-1.4873.517-2.716 1.552-3.686 1.034-1.0347 2.328-1.552 3.88-1.552h4.462v-6.208c0-1.6813.549-3.0717 1.649-4.171 1.164-1.164 2.586-1.746 4.268-1.746 1.616 0 2.974.582 4.074 1.746 1.099 1.0993 1.649 2.4897 1.649 4.171v6.208h6.887c1.552 0 2.845.5173 3.88 1.552 1.034 1.0347 1.552 2.328 1.552 3.88 0 1.4873-.518 2.7483-1.552 3.783-1.035.97-2.328 1.455-3.88 1.455h-6.887v26.287c0 1.358.355 2.3603 1.067 3.007.711.582 1.681.873 2.91.873.517 0 1.228-.1293 2.134-.388.776-.3233 1.519-.485 2.231-.485zm58.702-14.938c0 5.1733-1.164 9.8293-3.492 13.968-2.328 4.074-5.529 7.2427-9.603 9.506-4.009 2.2633-8.439 3.395-13.289 3.395-4.915 0-9.377-1.1317-13.386-3.395-4.009-2.2633-7.178-5.432-9.506-9.506-2.328-4.1387-3.492-8.7947-3.492-13.968 0-5.1733 1.164-9.797 3.492-13.871 2.328-4.1387 5.497-7.3397 9.506-9.603 4.009-2.328 8.471-3.492 13.386-3.492 4.85 0 9.28 1.164 13.289 3.492 4.074 2.2633 7.275 5.4643 9.603 9.603 2.328 4.074 3.492 8.6977 3.492 13.871zm-11.64 0c0-3.1687-.679-5.9817-2.037-8.439-1.293-2.522-3.072-4.462-5.335-5.82-2.199-1.358-4.656-2.037-7.372-2.037s-5.206.679-7.469 2.037c-2.199 1.358-3.977 3.298-5.335 5.82-1.293 2.4573-1.94 5.2703-1.94 8.439 0 3.1687.647 5.9817 1.94 8.439 1.358 2.4573 3.136 4.365 5.335 5.723 2.263 1.358 4.753 2.037 7.469 2.037s5.173-.679 7.372-2.037c2.263-1.358 4.042-3.2657 5.335-5.723 1.358-2.4573 2.037-5.2703 2.037-8.439zm44.354-26.966c4.462 0 8.504 1.164 12.125 3.492 3.621 2.2633 6.467 5.432 8.536 9.506 2.134 4.074 3.201 8.6977 3.201 13.871 0 5.1733-1.035 9.8293-3.104 13.968-2.069 4.074-4.915 7.275-8.536 9.603-3.557 2.2633-7.501 3.395-11.834 3.395-3.104 0-6.046-.6143-8.827-1.843-2.716-1.2933-4.979-2.8453-6.79-4.656v19.012c0 1.681-.55 3.072-1.649 4.171-1.099 1.164-2.49 1.746-4.171 1.746s-3.072-.55-4.171-1.649c-1.099-1.099-1.649-2.522-1.649-4.268V51.022c0-1.6813.55-3.0717 1.649-4.171 1.099-1.164 2.49-1.746 4.171-1.746s3.072.582 4.171 1.746c1.099 1.0993 1.649 2.4897 1.649 4.171v.388c1.552-1.94 3.718-3.6213 6.499-5.044 2.781-1.4873 5.691-2.231 8.73-2.231zM353.422 87.3c4.139 0 7.534-1.552 10.185-4.656 2.651-3.104 3.977-6.984 3.977-11.64 0-4.656-1.326-8.5037-3.977-11.543-2.587-3.104-5.982-4.656-10.185-4.656s-7.631 1.552-10.282 4.656c-2.651 3.0393-3.977 6.887-3.977 11.543s1.326 8.536 3.977 11.64c2.651 3.104 6.079 4.656 10.282 4.656zm58.011-43.165c4.462 0 8.503 1.164 12.125 3.492 3.621 2.2633 6.466 5.432 8.536 9.506 2.134 4.074 3.201 8.6977 3.201 13.871 0 5.1733-1.035 9.8293-3.104 13.968-2.07 4.074-4.915 7.275-8.536 9.603-3.557 2.2633-7.502 3.395-11.834 3.395-3.104 0-6.047-.6143-8.827-1.843-2.716-1.2933-4.98-2.8453-6.79-4.656v19.012c0 1.681-.55 3.072-1.649 4.171-1.1 1.164-2.49 1.746-4.171 1.746-1.682 0-3.072-.55-4.171-1.649-1.1-1.099-1.649-2.522-1.649-4.268V51.022c0-1.6813.549-3.0717 1.649-4.171 1.099-1.164 2.489-1.746 4.171-1.746 1.681 0 3.071.582 4.171 1.746 1.099 1.0993 1.649 2.4897 1.649 4.171v.388c1.552-1.94 3.718-3.6213 6.499-5.044 2.78-1.4873 5.69-2.231 8.73-2.231zM409.978 87.3c4.138 0 7.533-1.552 10.185-4.656 2.651-3.104 3.977-6.984 3.977-11.64 0-4.656-1.326-8.5037-3.977-11.543-2.587-3.104-5.982-4.656-10.185-4.656-4.204 0-7.631 1.552-10.282 4.656-2.652 3.0393-3.977 6.887-3.977 11.543s1.325 8.536 3.977 11.64c2.651 3.104 6.078 4.656 10.282 4.656zm80.611-17.46c-.064 1.552-.679 2.813-1.843 3.783-1.164.97-2.522 1.455-4.074 1.455h-32.01c.776 3.8153 2.555 6.8223 5.335 9.021 2.781 2.134 5.917 3.201 9.409 3.201 2.652 0 4.721-.2263 6.208-.679 1.488-.5173 2.652-1.0347 3.492-1.552.906-.582 1.52-.97 1.843-1.164 1.164-.582 2.264-.873 3.298-.873 1.358 0 2.522.485 3.492 1.455.97.97 1.455 2.1017 1.455 3.395 0 1.746-.905 3.3303-2.716 4.753-1.81 1.4873-4.235 2.7483-7.275 3.783-3.039 1.0347-6.111 1.552-9.215 1.552-5.432 0-10.185-1.1317-14.259-3.395-4.009-2.2633-7.113-5.3673-9.312-9.312-2.198-4.0093-3.298-8.5037-3.298-13.483 0-5.5613 1.164-10.4437 3.492-14.647 2.328-4.2033 5.4-7.4043 9.215-9.603 3.816-2.2633 7.89-3.395 12.222-3.395 4.268 0 8.278 1.164 12.028 3.492 3.816 2.328 6.855 5.4643 9.118 9.409 2.264 3.9447 3.395 8.2127 3.395 12.804zm-24.541-15.035c-7.501 0-11.931 3.5243-13.289 10.573h25.414v-.679c-.258-2.716-1.584-5.044-3.977-6.984-2.392-1.94-5.108-2.91-8.148-2.91zm58.111-10.67c6.596 0 11.155 1.9723 13.677 5.917 2.522 3.88 3.783 9.2797 3.783 16.199v24.832c0 1.6813-.55 3.104-1.649 4.268-1.1 1.0993-2.49 1.649-4.171 1.649-1.682 0-3.072-.5497-4.171-1.649-1.1-1.164-1.649-2.5867-1.649-4.268V66.251c0-3.5567-.744-6.3373-2.231-8.342-1.488-2.0693-4.042-3.104-7.663-3.104-3.751 0-6.693 1.0993-8.827 3.298-2.134 2.134-3.201 4.85-3.201 8.148v24.832c0 1.6813-.55 3.104-1.649 4.268-1.1 1.0993-2.49 1.649-4.171 1.649-1.682 0-3.072-.5497-4.171-1.649-1.1-1.164-1.649-2.5867-1.649-4.268V51.022c0-1.6813.549-3.0717 1.649-4.171 1.099-1.164 2.489-1.746 4.171-1.746 1.681 0 3.071.582 4.171 1.746 1.099 1.0993 1.649 2.4897 1.649 4.171v1.649c1.746-2.328 4.009-4.3327 6.79-6.014 2.845-1.6813 5.949-2.522 9.312-2.522z"
                />
                <path
                    id="pt-path1"
                    fillRule="evenodd"
                    d="M0 9.00366V12h12V0H7.65234C6.68848 4.08084 3.80078 7.41861 0 9.00366z"
                />
                <path
                    id="pt-path2"
                    d="M26 13c0 7.1797-5.8203 13-13 13S0 20.1797 0 13 5.8203 0 13 0s13 5.8203 13 13z"
                />
                <path
                    id="pt-path3"
                    d="M0 13C0 5.8203 5.8203 0 13 0s13 5.8203 13 13v54c0 7.1797-5.8203 13-13 13S0 74.1797 0 67V13z"
                />
                <path
                    id="pt-path4"
                    d="M0 13C0 5.8203 5.8203 0 13 0s13 5.8203 13 13v127c0 7.18-5.8203 13-13 13s-13-5.82-13-13V13z"
                />
            </defs>
        </svg>
    );
}

function useTheme() {
    const [dark, setDark] = useState<boolean | null>(null);

    useEffect(() => {
        let stored: string | null = null;
        try {
            stored = localStorage.getItem('dashboard-theme');
        } catch {
            // Storage can be blocked by browser privacy settings.
        }
        setDark(
            stored ? stored === 'dark' : window.matchMedia('(prefers-color-scheme: dark)').matches,
        );
    }, []);

    useEffect(() => {
        if (dark === null) return;
        document.documentElement.classList.toggle('dark', dark);
        try {
            localStorage.setItem('dashboard-theme', dark ? 'dark' : 'light');
        } catch {
            // The theme still works when the browser cannot persist it.
        }
    }, [dark]);

    return { dark: dark ?? false, toggle: () => setDark((d) => !d) };
}

export function MenuToggleIcon({ open }: { open: boolean }) {
    return (
        <div className="relative block h-[22px] w-[22px]">
            <span
                className={cn(
                    'absolute right-0 h-[2px] w-full bg-white transition-all duration-200',
                    open ? 'top-[10px] rotate-45' : 'top-[3px]',
                )}
            />
            <span
                className={cn(
                    'absolute right-0 top-[10px] h-[2px] w-[80%] bg-white transition-all duration-200',
                    open ? 'translate-x-6 opacity-0' : 'opacity-100',
                )}
            />
            <span
                className={cn(
                    'absolute right-0 h-[2px] bg-white transition-all duration-200',
                    open ? 'top-[10px] w-full -rotate-45' : 'top-[17px] w-[40%]',
                )}
            />
        </div>
    );
}

export function SiteMenuDrawer({
    categories,
    dark,
    open,
    onCookieSettings,
    onToggleTheme,
    showBoosts = false,
    user,
}: SiteMenuDrawerProps) {
    return (
        <nav
            id="dashboard-site-menu"
            aria-hidden={!open}
            className={cn(
                'fixed inset-0 z-40 overflow-y-auto overflow-x-hidden bg-black text-white transition-transform duration-300 ease-out',
                open ? 'translate-y-0' : '-translate-y-full pointer-events-none',
            )}
        >
            <div className="mx-auto flex min-h-full max-w-7xl flex-col px-4 pb-10 pt-28 sm:px-6 sm:pt-32 lg:px-8">
                <div className="mx-auto w-full max-w-6xl">
                    <h2
                        className="text-center text-3xl tracking-tight text-primary sm:text-4xl"
                        style={{ fontFamily: 'Lato, sans-serif', fontWeight: 100 }}
                    >
                        Kategorier
                    </h2>
                    {showBoosts && (
                        <Link
                            href={boosts.url()}
                            className="mt-8 flex items-center justify-between rounded-xl bg-white/10 px-4 py-3 text-base text-white sm:hidden"
                        >
                            Podcast Boosts
                            <ChevronRight className="h-5 w-5 flex-none text-white/50" />
                        </Link>
                    )}
                    <ul className="mt-6 divide-y divide-white/10 sm:mt-10 sm:grid sm:grid-cols-2 sm:divide-y-0 lg:grid-cols-3 xl:grid-cols-4">
                        {categories.map((category) => (
                            <li key={category.slug}>
                                <a
                                    href={`/kategori/${category.slug}`}
                                    className="flex min-h-[3.25rem] items-center justify-between gap-3 px-1 py-3 text-lg leading-snug text-white/90 transition-colors hover:bg-white/10 hover:text-white sm:block sm:min-h-0 sm:px-6 sm:py-4 sm:text-white/80"
                                    style={{ fontFamily: 'Lato, sans-serif', fontWeight: 100 }}
                                >
                                    <span className="min-w-0 break-words">{category.name}</span>
                                    <ChevronRight
                                        aria-hidden="true"
                                        className="h-5 w-5 flex-none text-white/40 sm:hidden"
                                    />
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="mt-auto pt-10">
                    {user && (
                        <div className="flex justify-center">
                            <Link
                                href="/logout"
                                method="post"
                                as="button"
                                className="inline-flex items-center rounded-full border border-white/15 px-5 py-2 text-sm text-white/80 transition-colors hover:border-white/30 hover:bg-white/10 hover:text-white"
                            >
                                Logga ut
                            </Link>
                        </div>
                    )}

                    <div className="mt-10 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-center text-sm text-white/70">
                        <a href="/om" className="transition-colors hover:text-white">
                            Om Poddtoppen
                        </a>
                        <span className="text-white/30">|</span>
                        <a href="/om-podcast" className="transition-colors hover:text-white">
                            Om Podcast
                        </a>
                        <span className="text-white/30">|</span>
                        <a
                            href="#"
                            className="transition-colors hover:text-white"
                            onClick={(event) => {
                                event.preventDefault();
                                onCookieSettings();
                            }}
                        >
                            Cookies
                        </a>
                        <span className="text-white/30">|</span>
                        <button
                            type="button"
                            title={dark ? 'Ljust läge' : 'Mörkt läge'}
                            aria-label={dark ? 'Växla till ljust läge' : 'Växla till mörkt läge'}
                            className="inline-flex items-center text-white/70 transition-colors hover:text-white"
                            onClick={onToggleTheme}
                        >
                            {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                        </button>
                    </div>

                    <p className="mt-3 text-center text-sm text-white/45">
                        Topplistorna hämtas med hjälp av iTunes.
                    </p>
                </div>
            </div>
        </nav>
    );
}

function FlashToast({
    message,
    variant,
    visible,
    onClose,
}: {
    message: string;
    variant: 'success' | 'error';
    visible: boolean;
    onClose: () => void;
}) {
    const isError = variant === 'error';

    return (
        <div
            aria-live="polite"
            className={cn(
                'pointer-events-none fixed bottom-0 left-0 right-0 z-[80] flex items-end px-4 py-6 transition duration-300 ease-out sm:items-start sm:p-6',
                visible
                    ? 'visible translate-y-0 opacity-100 sm:translate-x-0'
                    : 'invisible translate-y-2 opacity-0 sm:translate-x-2 sm:translate-y-0',
            )}
        >
            <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
                <div className="pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black/5">
                    <div className="p-4">
                        <div className="flex items-start">
                            <div
                                className={cn(
                                    'relative flex-shrink-0 rounded-full p-4',
                                    isError ? 'bg-red-500' : 'bg-primary',
                                )}
                            >
                                {isError ? (
                                    <svg
                                        viewBox="0 0 24 24"
                                        width="24"
                                        height="24"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="text-white"
                                    >
                                        <circle cx="12" cy="12" r="10" />
                                        <line x1="12" y1="8" x2="12" y2="12" />
                                        <line x1="12" y1="16" x2="12.01" y2="16" />
                                    </svg>
                                ) : (
                                    <>
                                        <svg
                                            viewBox="0 0 24 24"
                                            width="24"
                                            height="24"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            fill="none"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="text-white"
                                        >
                                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                            <circle cx="12" cy="7" r="4" />
                                        </svg>
                                        <svg
                                            viewBox="0 0 24 24"
                                            width="24"
                                            height="24"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            fill="none"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="absolute right-1 top-1 scale-50 animate-pulse fill-white text-primary"
                                        >
                                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                                        </svg>
                                    </>
                                )}
                            </div>
                            <div className="ml-3 w-0 flex-1 pt-0.5">
                                <p className="text-sm font-medium text-gray-900">
                                    {isError ? 'Något gick fel' : 'Klart'}
                                </p>
                                <p className="mt-1 text-sm text-gray-500">{message}</p>
                            </div>
                            <div className="ml-4 flex flex-shrink-0">
                                <button
                                    type="button"
                                    className="inline-flex rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                    onClick={onClose}
                                >
                                    <span className="sr-only">Close</span>
                                    <svg
                                        className="h-5 w-5"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                        aria-hidden="true"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M4.293 4.293a1 1 0 0 1 1.414 0L10 8.586l4.293-4.293a1 1 0 1 1 1.414 1.414L11.414 10l4.293 4.293a1 1 0 0 1-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 0 1-1.414-1.414L8.586 10 4.293 5.707a1 1 0 0 1 0-1.414z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function DashboardLayout({ children }: { children: ReactNode }) {
    const { dark, toggle } = useTheme();
    const { props, url } = usePage<DashboardPageProps>();
    const categories = props.siteMenu?.categories ?? [];
    const user = props.auth?.user ?? null;
    const [menuOpen, setMenuOpen] = useState(false);
    const [toast, setToast] = useState<FlashToastState | null>(null);
    const [toastVisible, setToastVisible] = useState(false);

    useEffect(() => {
        setMenuOpen(false);
    }, [url]);

    useEffect(() => {
        const success = props.flash?.success;
        const error = props.flash?.error;

        if (success) {
            setToast({
                variant: 'success',
                message: success,
                sequence: Date.now(),
            });
            setToastVisible(true);
            return;
        }

        if (error) {
            setToast({
                variant: 'error',
                message: error,
                sequence: Date.now(),
            });
            setToastVisible(true);
        }
    }, [props]);

    useEffect(() => {
        if (!toastVisible) {
            return;
        }

        const timeout = window.setTimeout(() => {
            setToastVisible(false);
        }, 8000);

        return () => {
            window.clearTimeout(timeout);
        };
    }, [toastVisible, toast?.sequence]);

    useEffect(() => {
        if (!menuOpen) {
            return;
        }

        const previousOverflow = document.body.style.overflow;
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setMenuOpen(false);
            }
        };

        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [menuOpen]);

    return (
        <div className="flex min-h-screen flex-col">
            <header
                className={cn(
                    'relative z-50 transition-colors',
                    menuOpen ? 'bg-black' : 'bg-primary',
                )}
            >
                <div className="mx-auto flex max-w-7xl items-center justify-between gap-2 px-4 py-4">
                    <a href="/" title="Poddtoppen" className="min-w-0 shrink">
                        <PoddtoppenLogo className="h-auto w-[140px] max-w-full sm:w-[180px]" />
                    </a>
                    <div className="flex flex-none items-center gap-1 sm:gap-2">
                        {props.features?.podcastBoost && (
                            <Link
                                href={boosts.url()}
                                className="hidden px-2 text-sm text-white sm:inline"
                            >
                                Podcast Boosts
                            </Link>
                        )}
                        <Link
                            href="/mina-sidor"
                            title="Mina sidor"
                            className="flex items-center justify-center rounded-full p-2 text-white transition-all hover:bg-black/10 sm:p-3"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="22"
                                height="22"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="block"
                            >
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                <circle cx="12" cy="7" r="4" />
                            </svg>
                        </Link>
                        <a
                            href="/sok"
                            title="Sök"
                            className="flex items-center justify-center rounded-full p-2 text-white transition-all hover:bg-black/10 sm:p-3"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="22"
                                height="22"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="block"
                            >
                                <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </a>
                        <button
                            type="button"
                            title="Meny"
                            aria-expanded={menuOpen}
                            aria-controls="dashboard-site-menu"
                            aria-label={menuOpen ? 'Stäng meny' : 'Öppna meny'}
                            className="flex items-center justify-center rounded-full p-2 text-white transition-all hover:bg-black/10 sm:p-3"
                            onClick={() => setMenuOpen((open) => !open)}
                        >
                            <MenuToggleIcon open={menuOpen} />
                        </button>
                    </div>
                </div>
            </header>
            <SiteMenuDrawer
                categories={categories}
                dark={dark}
                open={menuOpen}
                onCookieSettings={() => {
                    window.__tcfapi?.('displayConsentUi', 2, () => {});
                }}
                onToggleTheme={toggle}
                showBoosts={Boolean(props.features?.podcastBoost)}
                user={user}
            />
            <main className="flex-1">{children}</main>
            {toast && (
                <FlashToast
                    key={toast.sequence}
                    message={toast.message}
                    variant={toast.variant}
                    visible={toastVisible}
                    onClose={() => setToastVisible(false)}
                />
            )}
        </div>
    );
}
